import { RdsBadgeColor } from '@rds/core';

import {
  BookingHistoryApiResponseModel,
  BookingHistoryChangesModel,
  BookingHistoryEntryModel,
  BookingRequestDetailsApiResponseModel,
  BookingRequestDetailsModel,
  BookingStatusApiResponseEnum,
  PagedResponse,
  RoomBookingModel,
  RoomBookingsApiResponseModel,
} from '@bookly/shared';

export function roomBookingsConverter(
  roomBookings: PagedResponse<RoomBookingsApiResponseModel>
): PagedResponse<RoomBookingModel> {
  return {
    content: roomBookings.content.map(roomBooking => ({
      ...roomBooking,
      createTime: new Date(roomBooking.createTime).toLocaleString(['en-GB'], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      startDate: new Date(roomBooking.startTime),
      endDate: new Date(roomBooking.endTime),
      startTime: new Date(roomBooking.startTime).toLocaleString(['en-GB'], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      endTime: new Date(roomBooking.endTime).toLocaleString(['en-GB'], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      status: statusConverter(roomBooking.status, roomBooking.isReapproved),
    })),
    totalSize: roomBookings.totalSize,
  };
}

export function bookingDetailsConverter(
  bookingDetails: BookingRequestDetailsApiResponseModel
): BookingRequestDetailsModel {
  return {
    ...bookingDetails,
    startTime: new Date(bookingDetails.startTime),
    endTime: new Date(bookingDetails.endTime),
    createTime: new Date(bookingDetails.createTime),
    status: statusConverter(bookingDetails.status, bookingDetails.isReapproved),
  };
}

export function bookingDetailsListConverter(
  bookingDetails: BookingRequestDetailsApiResponseModel[]
): BookingRequestDetailsModel[] {
  return bookingDetails.map(booking => bookingDetailsConverter(booking));
}

export function bookingHistoryConverter(
  response: BookingHistoryApiResponseModel
): BookingHistoryEntryModel[] {
  const entries: BookingHistoryEntryModel[] = (response.revisions ?? []).map(
    revision => ({
      ...revision,
      updateDate: new Date(revision.updateDate),
    })
  );

  // Synthesize a "revision 0" entry from the initial booking snapshot
  if (response.initialRoomBooking) {
    const initial = response.initialRoomBooking;
    entries.unshift({
      revisionNumber: 0,
      updatedBy: initial.userEmail ?? initial.requester?.email ?? '',
      updateDate: new Date(initial.createTime ?? new Date().toISOString()),
      bookingStatus: initial.status ?? BookingStatusApiResponseEnum.InProgress,
      isReapproved: initial.isReapproved,
      changes: initialBookingToChanges(initial),
    });
  }

  return entries;
}

export function statusConverter(
  status: BookingStatusApiResponseEnum,
  isReapproved = false
): {
  value: BookingStatusApiResponseEnum;
  text: string;
  color: RdsBadgeColor;
} {
  switch (status) {
    case BookingStatusApiResponseEnum.InProgress:
      return {
        value: BookingStatusApiResponseEnum.InProgress,
        text: isReapproved ? 'Awaiting Re-Approval' : 'Awaiting Approval',
        color: isReapproved ? 'neutral' : 'yellow',
      };
    case BookingStatusApiResponseEnum.Approved:
      return {
        value: BookingStatusApiResponseEnum.Approved,
        text: 'Approved',
        color: 'green',
      };
    case BookingStatusApiResponseEnum.Rejected:
      return {
        value: BookingStatusApiResponseEnum.Rejected,
        text: 'Rejected',
        color: 'red',
      };
    case BookingStatusApiResponseEnum.Canceled:
      return {
        value: BookingStatusApiResponseEnum.Canceled,
        text: 'Canceled',
        color: 'pink',
      };
    case BookingStatusApiResponseEnum.Deleted:
      return {
        value: BookingStatusApiResponseEnum.Deleted,
        text: 'Deleted',
        color: 'orange',
      };
    default:
      return {
        value: BookingStatusApiResponseEnum.New,
        text: 'New',
        color: 'yellow',
      };
  }
}

/**
 * Converts a typed `BookingRequestDetailsApiResponseModel` snapshot into
 * `BookingHistoryChangesModel` where every value is a leaf with only `current`.
 * Fields are mapped explicitly to match the history component's template.
 */
function initialBookingToChanges(
  initial: BookingRequestDetailsApiResponseModel
): BookingHistoryChangesModel {
  const result: BookingHistoryChangesModel = {};
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null) {
      result[key] = { current: String(value) };
    }
  };

  set('eventName', initial.eventName);
  set('numberOfPeople', initial.numberOfPeople);
  set('costCenter', initial.costCenter);
  set('wbsCode', initial.wbsCode);
  set('userEmail', initial.userEmail);
  set('startTime', initial.startTime);
  set('endTime', initial.endTime);
  set('status', initial.status);
  set('customSetupTimeBefore', initial.customSetupTimeBefore);
  set('customSetupTimeAfter', initial.customSetupTimeAfter);
  set('additionalInfo', initial.additionalInfo);

  if (initial.services?.length) {
    result['services'] = {
      current: JSON.stringify(initial.services),
    };
  }

  if (initial.requester) {
    const group: BookingHistoryChangesModel = {};
    if (initial.requester.email != null) {
      group['email'] = { current: String(initial.requester.email) };
    }
    if (initial.requester.fullName != null) {
      group['fullName'] = { current: String(initial.requester.fullName) };
    }
    if (Object.keys(group).length > 0) {
      result['requester'] = group;
    }
  }

  if (initial.onBehalfParticipants?.length) {
    result['onBehalfParticipants'] = {
      current: initial.onBehalfParticipants.map(participant => ({
        email: participant.email,
        fullName: participant.fullName,
      })),
    };
  }

  if (initial.generalQuestions) {
    const gq = initial.generalQuestions;
    const group: BookingHistoryChangesModel = {};
    group['cecOnSite'] = { current: String(gq.cecOnSite) };
    group['hcpOnSite'] = { current: String(gq.hcpOnSite) };
    group['moreThan20Externals'] = {
      current: String(gq.moreThan20Externals),
    };
    result['generalQuestions'] = group;
  }

  if (initial.layout) {
    const l = initial.layout;
    const group: BookingHistoryChangesModel = {};
    if (l.roomLayoutName != null) {
      group['roomLayoutName'] = { current: String(l.roomLayoutName) };
    }
    if (initial.roomLayoutUUID != null) {
      group['roomLayoutUUID'] = { current: String(initial.roomLayoutUUID) };
    }
    if (l.setupBeforeMinutes != null) {
      group['setupBeforeMinutes'] = {
        current: String(l.setupBeforeMinutes),
      };
    }
    if (l.setupAfterMinutes != null) {
      group['setupAfterMinutes'] = {
        current: String(l.setupAfterMinutes),
      };
    }
    if (l.capacityMax != null) {
      group['capacityMax'] = { current: String(l.capacityMax) };
    }
    if (l.capacityMin != null) {
      group['capacityMin'] = { current: String(l.capacityMin) };
    }
    if (l.layoutSelectedTimeDate != null) {
      group['layoutSelectedTimeDate'] = {
        current: String(l.layoutSelectedTimeDate),
      };
    }
    if (Object.keys(group).length > 0) {
      result['layout'] = group;
    }
  }

  return result;
}
